import { Add } from '@mui/icons-material';
import { Button, Typography } from '@mui/material';
import { DrawerHeader } from '../../../../../drawer/DrawerHeader';
import { DocumentType, graphql } from '../../../../../gql';
import { useApiMutation, useApiQuery } from '../../../../../query/hooks';
import { ChangeEvent, FC, ReactNode, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { z } from 'zod';
import { PictureItem } from './PictureItem';
import { useImageUpload } from '../../../../../images/useImageUpload';
import { useSnackbar } from '../../../../../store/snackbar';
import { PictureEditDrawerGrid } from './PictureEditDrawerGrid';
import { DrawerWrapper } from '../../../../../drawer/DrawerWrapper';
import { isMobile } from 'react-device-detect';

const activityPicturesQuery = graphql(`
	query journeyDetail_getActivityPictures($activityId: UUID!) {
		getActivity(by: { id: $activityId }) {
			pictures {
				id
				order
				image {
					url(width: 270)
				}
			}
		}
	}
`);

const updatePictureMutation = graphql(`
	mutation picturesDrawer_connectPicutreToActivity($activityId: UUID!, $imageId: UUID!, $order: Int!) {
		createPicture(data: { activity: { connect: { id: $activityId } }, image: { connect: { id: $imageId } }, order: $order }) {
			ok
		}
	}
`);

export type PictureItemType = NonNullable<DocumentType<typeof activityPicturesQuery>['getActivity']>['pictures'][number];

const MAX_AMOUNT_OF_PICTURES = 6;

export const picturesEditDrawerSchema = z.object({
	activityId: z.string(),
});

export const PicturesEditDrawer: FC<z.infer<typeof picturesEditDrawerSchema>> = ({ activityId }) => {
	const intl = useIntl();
	const { openSnackbar } = useSnackbar();

	const [uploadingFiles, setUploadingFiles] = useState<ReactNode[]>([]);
	const [toUpload, setToUpload] = useState<File[]>([]);
	const { data, isLoading: isContentLoading } = useApiQuery(activityPicturesQuery, {
		variables: { activityId },
	});
	const { mutate: connectImageToActivity } = useApiMutation(updatePictureMutation, {
		onError: () => {
			openSnackbar({
				open: true,
				message: intl.formatMessage({ id: 'widget.pictures.connect.error' }),
				variant: 'alert',
				alert: {
					color: 'error',
				},
				close: true,
			});
		},
		onSettled: () => setUploadingFiles((prev) => prev.slice(1)),
	});

	const { uploadImage, isLoading } = useImageUpload({
		onSuccess: (imageId) => {
			connectImageToActivity({
				activityId,
				imageId,
				order: data?.getActivity?.pictures.length ?? 0,
			});
		},
		onError: () => {
			setUploadingFiles((prev) => prev.slice(1));
			openSnackbar({
				open: true,
				message: intl.formatMessage({ id: 'common.error.default' }),
				variant: 'alert',
				alert: {
					color: 'error',
				},
				close: true,
			});
		},
	});

	const [isUploadClicked, setIsUploadClicked] = useState(false);

	// Upload images one by one
	useEffect(() => {
		if (!isLoading) {
			const file = toUpload[0];
			// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
			if (file) {
				uploadImage(file);
				setToUpload((prev) => prev.slice(1));
			}
		}
		// No need to memoize uploadImage function
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoading, toUpload]);

	const checkCameraPermissions = async () => {
		try {
			const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
			// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
			if (stream != null) {
				return;
			}
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
		} catch (error) {
			openSnackbar({
				open: true,
				message: intl.formatMessage({ id: 'drawer.pictures.camera-permissions.error' }),
				variant: 'alert',
				alert: {
					color: 'info',
				},
				close: true,
			});
		}
	};

	useEffect(() => {
		if (isMobile) {
			checkCameraPermissions();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isUploadClicked]);

	const handleUpload = (event: ChangeEvent<HTMLInputElement>) => {
		const imagesToUploadList = event.target.files;

		if (imagesToUploadList == null || imagesToUploadList.length === 0) {
			return;
		}

		const imagesToUpload = Array.from(imagesToUploadList);

		if (data?.getActivity?.pictures && data.getActivity.pictures.length + imagesToUpload.length > MAX_AMOUNT_OF_PICTURES) {
			openSnackbar({
				open: true,
				message: intl.formatMessage({ id: 'journey.detail.widget.pictures.max-amount' }),
				variant: 'alert',
				alert: { color: 'error', severity: 'info' },
				close: true,
			});
			return;
		}

		const picturesToUpload = [...imagesToUpload].map((image) => {
			return <PictureItem isProcessed key={image.name} picture={{ image: { url: URL.createObjectURL(image) } }} />;
		});
		setUploadingFiles(picturesToUpload);
		setToUpload(imagesToUpload);
		event.target.value = ''; // resets files of the input
	};

	const pictures = data?.getActivity?.pictures ?? [];

	return (
		<DrawerWrapper>
			<DrawerHeader title={intl.formatMessage({ id: 'journey.detail.widget.pictures.title' })} />
			<PictureEditDrawerGrid
				activityId={activityId}
				isContentLoading={isContentLoading}
				uploadingFiles={uploadingFiles}
				pictures={[...pictures].sort((a, b) => a.order - b.order)}
			/>

			<Button
				startIcon={<Add />}
				component={'label'}
				variant={'contained'}
				color={'secondary'}
				fullWidth
				onClick={() => setIsUploadClicked(true)}
			>
				<FormattedMessage id={'common.upload'} />
				<input type={'file'} accept={'image/*'} multiple hidden onChange={handleUpload} id={'file-input'} />
			</Button>

			<Typography fontWeight={700} display={{ lg: 'none', xs: 'block' }}>
				<FormattedMessage id={'journey.detail.widget.pictures.check-permissions'} /> 
			</Typography>
		</DrawerWrapper>
	);
};
